// §4.2 Contacts & channels tab (Module 09, P3-A part 2).
//
// One Card per contact: name, position, the primary chip, the archived chip,
// Edit/Archive/Restore, the contact's channel rows (kind, the address in its
// MASKED form, verified + consent chips), per-channel remove, and an
// add-channel form.
//
// THE ADDRESS IS PAINTED MASKED FOR EVERY READER (spec 09 §4.2, D-307). The
// wire serves a holder of `messaging.address.read` the stored, normalized
// value and everyone else spec 17 §7's mask; this list paints both through
// `displayAddress`, the same display mask §4.5's link widget beside it and
// the inbox paint, so one address reads one way on every office screen. The
// full value stays on the wire for a holder, never on a shared screen.
//
// ARCHIVAL SEMANTICS, honestly (§2.3 write-back): archiving a contact clears
// its primary flag AND hard-deletes its channels server-side. The editor
// refreshes the 360 and the channels VANISH — no ghost rows, and the archive
// control carries a hint saying exactly that BEFORE the press, because a
// one-way consequence disclosed as a receipt is the D-069 failure shape.
//
// PER-VERB refusals (the task-#105 idiom): each mutation renders its own
// MutationFailureNote and every press resets its siblings, so a stale refusal
// never outlives the next action. 409 IDENTITY_TAKEN surfaces the server's
// own title; 422 CUSTOMER_ARCHIVED renders as the server's own sentence too —
// the server said it, the note labels it.
//
// Module 17's half ARRIVED with P3-B part 2: each contact card embeds the
// §4.5 link widget (messaging/components/link-widget.tsx) — linked status per
// provider, the one-time link-code modal with QR + countdown, and revoke. The
// stated-pending sentence this file carried until then is gone with it.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { MutationFailureNote } from "../mutation-failure-note";
import { LinkWidget } from "../../messaging/components/link-widget";
import { displayAddress } from "../../messaging/lib/format";
import { isAdminLineage, useSalesPermissions } from "../hooks/permissions";
import {
  useAddChannel,
  useCreateContact,
  useRemoveChannel,
  useUpdateContact,
  type ContactChannel,
  type CustomerContact,
} from "../hooks/sales-queries";

const CHANNEL_KINDS = ["phone", "telegram", "whatsapp", "email"] as const;

export function ContactEditor({
  customerId,
  customerStatus,
  contacts,
  channels,
}: {
  customerId: string;
  customerStatus: string;
  contacts: readonly CustomerContact[];
  channels: readonly ContactChannel[];
}) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const canManage = perms.has("sales.manage_customers");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<CustomerContact | undefined>(undefined);

  const create = useCreateContact();
  const update = useUpdateContact();

  return (
    <div className="sales-contacts" data-testid="sales-contacts">
      <div className="sales-toolbar">
        {canManage && customerStatus === "active" ? (
          <Button
            variant="primary"
            size="sm"
            data-testid="sales-contact-add"
            onClick={() => {
              setAdding(true);
            }}
          >
            {t("sales.contacts.add")}
          </Button>
        ) : null}
      </div>

      {create.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.contacts.createFailed")}
          error={create.error}
        />
      ) : null}
      {update.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.contacts.updateFailed")}
          error={update.error}
        />
      ) : null}

      {contacts.length === 0 ? (
        <p className="sales-hint" data-testid="sales-contacts-empty">
          {t("sales.contacts.none")}
        </p>
      ) : (
        contacts.map((contact) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            channels={channels.filter((ch) => ch.contact_id === contact.id)}
            customerId={customerId}
            canManage={canManage}
            canRemoveChannel={isAdminLineage(perms)}
            onEdit={() => {
              setEditing(contact);
            }}
            onArchiveToggle={(archived) => {
              create.reset();
              update.mutate({
                id: contact.id,
                customerId,
                body: { status: archived ? "archived" : "active" },
              });
            }}
          />
        ))
      )}

      {adding ? (
        <ContactModal
          heading={t("sales.contacts.addTitle")}
          busy={create.isPending}
          initial={undefined}
          onClose={() => {
            create.reset();
            setAdding(false);
          }}
          onSubmit={(fullName, position, isPrimary) => {
            update.reset();
            create.mutate(
              {
                customerId,
                body: {
                  full_name: fullName,
                  ...(position.length > 0 ? { position } : {}),
                  ...(isPrimary ? { is_primary: true } : {}),
                },
              },
              {
                onSuccess: () => {
                  setAdding(false);
                },
              },
            );
          }}
        />
      ) : null}

      {editing !== undefined ? (
        <ContactModal
          heading={t("sales.contacts.editTitle")}
          busy={update.isPending}
          initial={editing}
          onClose={() => {
            update.reset();
            setEditing(undefined);
          }}
          onSubmit={(fullName, position, isPrimary) => {
            create.reset();
            update.mutate(
              {
                id: editing.id,
                customerId,
                body: {
                  full_name: fullName,
                  position: position.length > 0 ? position : null,
                  is_primary: isPrimary,
                },
              },
              {
                onSuccess: () => {
                  setEditing(undefined);
                },
              },
            );
          }}
        />
      ) : null}
    </div>
  );
}

function ContactCard({
  contact,
  channels,
  customerId,
  canManage,
  canRemoveChannel,
  onEdit,
  onArchiveToggle,
}: {
  contact: CustomerContact;
  channels: readonly ContactChannel[];
  customerId: string;
  canManage: boolean;
  canRemoveChannel: boolean;
  onEdit: () => void;
  onArchiveToggle: (archived: boolean) => void;
}) {
  const { t } = useTranslation();
  const archived = contact.status === "archived";
  const addChannel = useAddChannel();
  const removeChannel = useRemoveChannel();
  const [kind, setKind] = useState<(typeof CHANNEL_KINDS)[number]>("phone");
  const [identity, setIdentity] = useState("");

  return (
    <Card
      className="sales-contact-card"
      data-testid="sales-contact-card"
      title={
        <>
          {contact.full_name}
          {contact.position !== null && contact.position.length > 0 ? (
            <span className="sales-360-fact"> · {contact.position}</span>
          ) : null}
        </>
      }
      action={
        <>
          {contact.is_primary ? (
            <StatusChip variant="accent" dot={false} data-testid="sales-contact-primary">
              {t("sales.contacts.primary")}
            </StatusChip>
          ) : null}
          {archived ? (
            <StatusChip variant="neutral" data-testid="sales-contact-archived">
              {t("sales.contacts.archived")}
            </StatusChip>
          ) : null}
          {canManage && !archived ? (
            <Button size="sm" data-testid="sales-contact-edit" onClick={onEdit}>
              {t("sales.common.edit")}
            </Button>
          ) : null}
          {canManage ? (
            <Button
              size="sm"
              variant={archived ? "secondary" : "danger"}
              data-testid="sales-contact-archive"
              onClick={() => {
                addChannel.reset();
                removeChannel.reset();
                onArchiveToggle(!archived);
              }}
            >
              {archived ? t("sales.contacts.restore") : t("sales.contacts.archive")}
            </Button>
          ) : null}
        </>
      }
    >
      {canManage && !archived ? (
        <p className="sales-hint">{t("sales.contacts.archiveHint")}</p>
      ) : null}

      {channels.length === 0 ? (
        <p className="sales-hint">{t("sales.contacts.noChannels")}</p>
      ) : (
        <ul className="sales-channel-list">
          {channels.map((channel) => (
            <li key={channel.id} className="sales-channel-row" data-testid="sales-channel-row">
              <span className="sales-channel-kind">{t(`sales.channel.${channel.channel}`)}</span>
              <code className="sales-channel-id" data-testid="sales-channel-address">
                {displayAddress(channel.channel, channel.identity_value)}
              </code>
              <StatusChip variant={channel.is_verified ? "ok" : "neutral"} dot={false}>
                {channel.is_verified
                  ? t("sales.contacts.verified")
                  : t("sales.contacts.unverified")}
              </StatusChip>
              <StatusChip
                variant={
                  channel.consent_status === "opted_in"
                    ? "ok"
                    : channel.consent_status === "opted_out"
                      ? "bad"
                      : "neutral"
                }
                dot={false}
              >
                {t(`sales.consent.${channel.consent_status}`)}
              </StatusChip>
              {canRemoveChannel ? (
                <Button
                  size="sm"
                  variant="danger"
                  data-testid="sales-channel-remove"
                  disabled={removeChannel.isPending}
                  onClick={() => {
                    addChannel.reset();
                    removeChannel.mutate({ id: channel.id, customerId });
                  }}
                >
                  {t("sales.contacts.remove")}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {addChannel.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.contacts.channelAddFailed")}
          error={addChannel.error}
        />
      ) : null}
      {removeChannel.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.contacts.channelRemoveFailed")}
          error={removeChannel.error}
        />
      ) : null}

      {canManage && !archived ? (
        <div className="sales-channel-add">
          <Field label={t("sales.contacts.channelKind")}>
            <Select
              value={kind}
              data-testid="sales-channel-kind"
              onChange={(e) => {
                setKind(e.target.value as (typeof CHANNEL_KINDS)[number]);
              }}
            >
              {CHANNEL_KINDS.map((value) => (
                <option key={value} value={value}>
                  {t(`sales.channel.${value}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("sales.contacts.identity")} wide>
            <input
              value={identity}
              data-testid="sales-channel-identity"
              onChange={(e) => {
                setIdentity(e.target.value);
              }}
            />
          </Field>
          <Button
            size="sm"
            data-testid="sales-channel-add"
            disabled={addChannel.isPending || identity.trim().length === 0}
            onClick={() => {
              removeChannel.reset();
              addChannel.mutate(
                {
                  contactId: contact.id,
                  customerId,
                  body: { channel: kind, identity_value: identity.trim() },
                },
                {
                  onSuccess: () => {
                    setIdentity("");
                  },
                },
              );
            }}
          >
            {t("sales.contacts.addChannel")}
          </Button>
        </div>
      ) : null}

      {/* Module 17 §4.5 — the messaging identity block, one per contact. The
          widget gates itself (renders nothing without the §7 codes) and stays
          off archived contacts: an archived contact's channels are already
          hard-deleted, and offering a link code for one would mint a handshake
          to a person the record says is gone. */}
      {!archived ? <LinkWidget ownerKind="customer_contact" ownerId={contact.id} /> : null}
    </Card>
  );
}

function ContactModal({
  heading,
  busy,
  initial,
  onClose,
  onSubmit,
}: {
  heading: string;
  busy: boolean;
  initial: CustomerContact | undefined;
  onClose: () => void;
  onSubmit: (fullName: string, position: string, isPrimary: boolean) => void;
}) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState(initial?.full_name ?? "");
  const [position, setPosition] = useState(initial?.position ?? "");
  const [isPrimary, setIsPrimary] = useState(initial?.is_primary ?? false);

  return (
    <Modal
      heading={heading}
      label={heading}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-contact-modal"
    >
      <div className="sales-form">
        <Field label={t("sales.contacts.fullName")} wide>
          <input
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.contacts.position")} wide>
          <input
            value={position}
            onChange={(e) => {
              setPosition(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.contacts.primaryLabel")}>
          <Select
            value={isPrimary ? "yes" : "no"}
            onChange={(e) => {
              setIsPrimary(e.target.value === "yes");
            }}
          >
            <option value="no">{t("sales.contacts.primaryNo")}</option>
            <option value="yes">{t("sales.contacts.primaryYes")}</option>
          </Select>
        </Field>
      </div>
      <div className="sales-modal-actions">
        <Button onClick={onClose}>{t("sales.common.cancel")}</Button>
        <Button
          variant="primary"
          data-testid="sales-contact-save"
          busy={busy}
          busyLabel={t("sales.common.saving")}
          disabled={fullName.trim().length === 0}
          onClick={() => {
            onSubmit(fullName.trim(), position.trim(), isPrimary);
          }}
        >
          {t("sales.common.save")}
        </Button>
      </div>
    </Modal>
  );
}
